/**
 * Wrapper for every RESIDENT-facing page root (IntakePage, ConfirmationPage, TrackingPage,
 * SubscribePage, NotifyLandingPage). Residents get ONE branded look pulled from the town's site —
 * no light/dark toggle (MVP dropped the toggle app-wide). This applies the derived palette
 * (theme/applyTheme.ts residentThemeVars) as inline CSS custom properties on the page's own
 * top-level element, so its derived neutrals stay scoped to the resident form and never leak into —
 * or are leaked on by — the console's :root vars, even though both mount under one shared ThemeProvider.
 *
 * Usage: replace `<div className="td-intake ...">` with `<ResidentThemeRoot className="td-intake ...">`.
 * Forwards all other div props (children required; className optional).
 */
import { forwardRef } from "react";
import type { HTMLAttributes, JSX } from "react";
import { useTheme } from "../theme/ThemeProvider.js";
import { residentThemeVars } from "../theme/applyTheme.js";
import { useBrandFontStacks } from "../theme/fonts.js";

interface Props extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const ResidentThemeRoot = forwardRef<HTMLDivElement, Props>(function ResidentThemeRoot(
  { children, style, ...rest },
  ref,
): JSX.Element {
  const { config } = useTheme();
  // Self-hosted-or-system font stacks (theme/fonts.ts) — starts system, upgrades once the town's
  // self-hosted webfont CSS resolves; never a stack naming an unloaded font.
  const fontStacks = useBrandFontStacks(config.fonts);
  const vars = residentThemeVars(config, fontStacks);
  return (
    <div
      {...rest}
      ref={ref}
      // `background` AND `color` are set explicitly (not just the --td-paper/--td-ink custom
      // properties) because this wrapper is a child of <body>: body's own background + text color
      // (the global :root palette, shared with the staff console and flipped to near-white ink when
      // the OS is in dark mode via :root[data-theme="dark"]) would otherwise be inherited by any
      // descendant without its own explicit color — e.g. the intake <h1>, which had no `color` rule
      // and rendered near-white on the light branded background. Pinning `color` to the derived
      // (always-AA) resident ink keeps the resident surface readable regardless of the OS theme.
      // CHROMELESS embed (meetings-embed-launcher dispatch): `.td-intake--chromeless` in app.css
      // overrides background to transparent for the flush-on-host-page look — the inline `background`
      // set here is a default, not `!important`, so that CSS class rule wins.
      style={{ ...vars, background: vars["--td-paper"], color: vars["--td-ink"], ...style } as React.CSSProperties}
    >
      {children}
    </div>
  );
});
